import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, BadgeCheck } from "lucide-react";
import { useSiteContent } from "@/context/ContentContext";
import { useReviews } from "@/hooks/usePublicContent";

export default function Testimonials() {
  const { testimonials: STATIC } = useSiteContent();
  const { items } = useReviews();
  const [active, setActive] = useState(0);

  // Prefer verified/approved reviews from the DB; fall back to static content.
  const reviews = items.length
    ? items.map((r) => ({ quote: r.text, name: r.author, role: [r.suburb, r.source].filter(Boolean).join(" · "), rating: r.rating, verified: r.verified }))
    : STATIC.map((t) => ({ quote: t.quote, name: t.name, role: t.role, rating: 5, verified: false }));

  if (!reviews.length) return null;
  const idx = Math.min(active, reviews.length - 1);
  const t = reviews[idx];

  return (
    <section id="studio" data-testid="testimonials-section" className="relative bg-[#f1f5f9] py-24 md:py-40 border-y border-[#0f172a]/10">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12 text-center">
        <p className="overline !text-[#9c7a4d] mb-8">Our Happy Patrons</p>
        <div className="flex items-center justify-center gap-1 mb-8" aria-label={`${t.rating} star rating`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={`h-5 w-5 ${i < t.rating ? "text-[#f59e0b] fill-[#f59e0b]" : "text-[#0f172a]/20"}`} />
          ))}
        </div>
        <Quote className="h-9 w-9 text-[#9c7a4d]/40 mx-auto mb-8" />

        <div className="min-h-[220px] md:min-h-[240px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-light text-[#0f172a] text-2xl md:text-4xl lg:text-[2.75rem] leading-[1.25] italic"
            >
              “{t.quote}”
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-10">
          <p className="text-[#0f172a] text-lg flex items-center justify-center gap-2">
            {t.name}
            {t.verified ? <BadgeCheck className="h-4 w-4 text-[#9c7a4d]" title="Verified review" /> : null}
          </p>
          {t.role ? <p className="overline !text-[#94a3b8] mt-1">{t.role}</p> : null}
        </div>

        <div className="flex items-center justify-center gap-3 mt-12">
          {reviews.map((_, i) => (
            <button
              key={i}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => setActive(i)}
              aria-label={`Review ${i + 1}`}
              className={`h-[3px] transition-all duration-500 ${idx === i ? "w-10 bg-[#9c7a4d]" : "w-5 bg-[#0f172a]/20 hover:bg-[#0f172a]/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
